import { useState } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { InstallGuideModal } from './InstallGuideModal';
import { useAuth } from '@/contexts/AuthContext';
import { safeStorage } from '@/lib/safeStorage';

const STORAGE_KEY = 'pwa_install_cta_dismissed';

export function InstallCTA() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isInstalled, isInstallable, isIOS, triggerInstall } = usePWAInstall();
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    return safeStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [isAnimating, setIsAnimating] = useState(true);

  // Hide if not logged in, already installed, not installable, or dismissed
  if (!user || isInstalled || !isInstallable || isDismissed) {
    return null;
  }

  const handleInstall = async () => {
    const result = await triggerInstall();
    
    if (result === 'show-guide') {
      setShowGuideModal(true);
    }
    // If installed or dismissed, CTA will hide automatically
  };

  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => {
      safeStorage.setItem(STORAGE_KEY, 'true');
      setIsDismissed(true);
    }, 300);
  };

  return (
    <>
      <div
        className={`fixed bottom-20 left-4 right-4 z-50 transition-all duration-300 ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        }`}
      >
        <div className="max-w-md mx-auto bg-card border border-border rounded-xl shadow-lg p-3 flex items-center gap-3">
          {/* App Icon */}
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center flex-shrink-0">
            <Download size={24} className="text-primary-foreground" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground text-sm">
              {t('pwa.installCTATitle', 'Install Gemurhood App')}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {t('pwa.installCTADesc', 'Fast access & offline support')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={handleInstall}
              size="sm"
              className="h-9 px-4"
            >
              {t('pwa.install', 'Install')}
            </Button>
            <button
              onClick={handleDismiss}
              className="p-1.5 hover:bg-muted rounded-full transition-colors"
              aria-label={t('common.close', 'Close')}
            >
              <X size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>

      <InstallGuideModal
        open={showGuideModal}
        onOpenChange={setShowGuideModal}
        isIOS={isIOS}
      />
    </>
  );
}
